import { getAsaasHandoff } from './handoff';
import { isAsaasPendingPaymentExpired, readPendingAsaasPayment } from './pending';
import { asaasBrowserReturnChannelName } from './returnHandoff';
import { ASAAS_PENDING_TTL_MS } from './types';
import { ensureAsaasPaymentWatcher } from './watcher';

const browserReturnListeners = new Map<
  string,
  { channel: BroadcastChannel; timeout: ReturnType<typeof setTimeout> }
>();
const desktopReturnListeners = new Map<
  string,
  { listener: EventListener; timeout: ReturnType<typeof setTimeout> }
>();

function registerAsaasBrowserReturnListener(returnId: string): void {
  if (getAsaasHandoff() !== 'browser' || typeof BroadcastChannel === 'undefined') return;
  if (browserReturnListeners.has(returnId)) return;
  const channel = new BroadcastChannel(asaasBrowserReturnChannelName(returnId));
  channel.addEventListener('message', (event) => {
    const data = event.data as { type?: unknown; returnId?: unknown } | null;
    if (data?.type !== 'asaas-return' || data.returnId !== returnId) return;
    const pending = readPendingAsaasPayment(returnId);
    if (!pending || isAsaasPendingPaymentExpired(pending)) return;
    ensureAsaasPaymentWatcher(pending.orderCode).requestCheck();
    channel.postMessage({ type: 'asaas-return-ack', returnId });
  });
  const timeout = setTimeout(() => {
    channel.close();
    browserReturnListeners.delete(returnId);
  }, ASAAS_PENDING_TTL_MS);
  browserReturnListeners.set(returnId, { channel, timeout });
}

function registerAsaasDesktopReturnListener(returnId: string): void {
  if (getAsaasHandoff() !== 'desktop' || desktopReturnListeners.has(returnId)) return;
  const listener: EventListener = (event) => {
    const value = (event as CustomEvent<{ returnId?: unknown }>).detail?.returnId;
    if (value !== returnId) return;
    const pending = readPendingAsaasPayment(returnId);
    if (!pending || isAsaasPendingPaymentExpired(pending)) return;
    ensureAsaasPaymentWatcher(pending.orderCode).requestCheck();
  };
  window.addEventListener('fett:asaas-payment-return', listener);
  const timeout = setTimeout(() => {
    window.removeEventListener('fett:asaas-payment-return', listener);
    desktopReturnListeners.delete(returnId);
  }, ASAAS_PENDING_TTL_MS);
  desktopReturnListeners.set(returnId, { listener, timeout });
}

export function registerAsaasReturnListener(returnId: string): void {
  if (getAsaasHandoff() === 'desktop') registerAsaasDesktopReturnListener(returnId);
  else registerAsaasBrowserReturnListener(returnId);
}
